import { useSyncExternalStore } from 'react';
import { getCardsForSpeech, getServerTTS } from './api/content';
import { logListen } from './api/user';
import { localeFor } from './i18n';
import { getPrefs } from './prefs';

/**
 * Dinleme kuyruğu. Varsayılan ses tarayıcının speechSynthesis'i (ücretsiz,
 * her yerde). Premium kullanıcıda önce sunucu TTS (Gemini) denenir; kota ya
 * da hata olursa sessizce tarayıcı sesine düşülür.
 */
export type Track = { contentId: string; title: string; source?: string | null; image?: string | null };

type State = {
  queue: Track[];
  index: number;
  playing: boolean;
  loading: boolean;
  /** Tarayıcı sesinde okunan parça / toplam parça. */
  part: number;
  parts: number;
  mode: 'speech' | 'audio';
};

type Ctx = { lang: string; premium: boolean; userId: string | null };

const initial: State = { queue: [], index: 0, playing: false, loading: false, part: 0, parts: 0, mode: 'speech' };
let state: State = initial;
let ctx: Ctx = { lang: 'en', premium: false, userId: null };
const listeners = new Set<() => void>();
let audio: HTMLAudioElement | null = null;
let session = 0;
let startedAt = 0;
let sentences: string[] = [];

function set(patch: Partial<State>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

export function usePlayer(): State {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => state
  );
}

export const speechSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

function pickVoice(lang: string): SpeechSynthesisVoice | null {
  const voices = window.speechSynthesis.getVoices();
  const locale = localeFor(lang).toLowerCase();
  const exact = voices.filter((v) => v.lang.toLowerCase() === locale);
  const loose = voices.filter((v) => v.lang.toLowerCase().startsWith(lang));
  const pool = exact.length ? exact : loose;
  // Doğal/premium sesleri öne al (Chrome "Google", Safari "Enhanced"/"Premium").
  return pool.find((v) => /natural|premium|enhanced|google/i.test(v.name)) ?? pool[0] ?? null;
}

function splitSentences(text: string): string[] {
  const out: string[] = [];
  for (const chunk of text.split(/(?<=[.!?…])\s+/)) {
    const c = chunk.trim();
    if (!c) continue;
    // Chrome uzun ifadeleri ~15 sn'de kesiyor; parçaları kısa tut.
    if (c.length > 220) out.push(...(c.match(/.{1,200}(\s|$)/g) ?? [c]).map((s) => s.trim()));
    else out.push(c);
  }
  return out;
}

function stopOutputs() {
  if (speechSupported) {
    // Chrome, duraklatılmış sentezi cancel() sonrasında da duraklatılmış
    // tutar; yeni ifadeler kuyruğa girer ama hiç okunmaz.
    if (window.speechSynthesis.paused) window.speechSynthesis.resume();
    window.speechSynthesis.cancel();
  }
  if (audio) {
    audio.onended = null;
    audio.onerror = null;
    audio.pause();
    audio.removeAttribute('src');
    audio = null;
  }
}

async function playIndex(i: number) {
  const my = ++session;
  stopOutputs();
  const track = state.queue[i];
  if (!track) {
    set({ playing: false, loading: false });
    return;
  }
  set({ index: i, loading: true, playing: true, part: 0, parts: 0 });
  startedAt = Date.now();

  if (ctx.premium) {
    try {
      const url = await getServerTTS(track.contentId, ctx.lang);
      if (my !== session) return;
      const el = new Audio(url);
      audio = el;
      el.playbackRate = getPrefs().speechRate;
      el.onended = () => onTrackEnd(my);
      el.onerror = () => {
        if (my !== session) return;
        stopOutputs();
        void playSpeech(my, track);
      };
      set({ loading: false, mode: 'audio' });
      // Yükleme sırasında duraklatıldıysa çalmaya başlama.
      if (state.playing) await el.play();
      return;
    } catch {
      if (my !== session) return;
      stopOutputs();
    }
  }
  await playSpeech(my, track);
}

async function playSpeech(my: number, track: Track) {
  if (!speechSupported) {
    set({ playing: false, loading: false });
    return;
  }
  const texts = await getCardsForSpeech(track.contentId, ctx.lang).catch(() => [track.title]);
  if (my !== session) return;
  sentences = texts.flatMap(splitSentences);
  set({ loading: false, mode: 'speech', parts: sentences.length, part: 0 });
  if (state.playing) speakFrom(my, 0);
}

function speakFrom(my: number, k: number) {
  if (my !== session) return;
  if (k >= sentences.length) {
    onTrackEnd(my);
    return;
  }
  const u = new SpeechSynthesisUtterance(sentences[k]);
  const voice = pickVoice(ctx.lang);
  if (voice) u.voice = voice;
  u.lang = voice?.lang ?? localeFor(ctx.lang);
  u.rate = getPrefs().speechRate;
  u.onend = () => {
    if (my !== session || !state.playing) return;
    set({ part: k + 1 });
    speakFrom(my, k + 1);
  };
  u.onerror = (e) => {
    if (e.error === 'interrupted' || e.error === 'canceled') return;
    speakFrom(my, k + 1);
  };
  window.speechSynthesis.speak(u);
}

function onTrackEnd(my: number) {
  if (my !== session) return;
  const track = state.queue[state.index];
  if (track && ctx.userId) void logListen(ctx.userId, track.contentId, (Date.now() - startedAt) / 1000).catch(() => undefined);
  if (state.index + 1 < state.queue.length) void playIndex(state.index + 1);
  else set({ playing: false, part: 0 });
}

export const player = {
  configure(next: Partial<Ctx>) {
    ctx = { ...ctx, ...next };
  },
  play(tracks: Track[], start = 0) {
    set({ queue: tracks });
    void playIndex(start);
  },
  toggle() {
    if (state.queue.length === 0) return;
    if (state.playing) {
      if (audio) audio.pause();
      else if (speechSupported) window.speechSynthesis.pause();
      set({ playing: false });
    } else {
      if (audio) void audio.play().catch(() => undefined);
      else if (speechSupported && window.speechSynthesis.paused) window.speechSynthesis.resume();
      else if (state.mode === 'speech' && sentences.length > 0 && !window.speechSynthesis.speaking) speakFrom(session, state.part);
      else void playIndex(state.index);
      set({ playing: true });
    }
  },
  next() {
    if (state.index + 1 < state.queue.length) void playIndex(state.index + 1);
  },
  prev() {
    void playIndex(Math.max(0, state.index - 1));
  },
  jump(i: number) {
    void playIndex(i);
  },
  stop() {
    session++;
    stopOutputs();
    set(initial);
  },
  isCurrent(contentId: string) {
    return state.queue[state.index]?.contentId === contentId;
  },
  setRate(rate: number) {
    if (audio) audio.playbackRate = rate;
  },
};

if (speechSupported) {
  // Sesler asenkron yüklenir; ilk çağrıda boş liste dönmesin.
  window.speechSynthesis.getVoices();
  window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
}
